import { useState } from 'react';
import {
  Box,
  Button,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Spinner,
  Text,
} from '@optiaxiom/react';
import { IconXmark } from '@optimizely/axiom-icons';

import { ArtifactContainer, ArtifactProvider, Artifacts } from '@frontend/components/Artifact';
import { ConfirmationModal } from '@frontend/components/confirmationModal/components/ConfirmationModal';
import { MarkdownText } from '@frontend/components/MarkdownText';
import { AgentExecutionItem } from '@frontend/types/agentExecution';
import { StepExecution } from '@frontend/types/stepExecution';

import {
  useExecutionDetails,
  useMemoryChunks,
  useMemoryDetails,
} from '../../hooks/useAgentExecutions';
import { flattenArtifacts } from '../../utils/artifactUtils';
import { getTotalTokenUsageForWorkflow } from '../../utils/executionUtils';
import { TokenUsageSection } from '../shared/TokenUsageSection';
import { EmptyStateMessage } from '../specialized-agent/shared/EmptyStateMessage';
import { ExecutionOutputDisplay } from '../specialized-agent/shared/ExecutionOutputDisplay';
import { ExecutionStatusBadge } from '../specialized-agent/shared/ExecutionStatusBadge';
import { MemoryChunksDisplay } from '../specialized-agent/shared/MemoryChunksDisplay';

type ExecutionDetailsSidebarProps = {
  execution: AgentExecutionItem;
  stepExecution?: StepExecution;
  onClose: () => void;
  onCancel?: () => void;
  isCancelling?: boolean;
};

export function ExecutionDetailsSidebar({
  execution,
  onClose,
  onCancel,
  isCancelling = false,
}: ExecutionDetailsSidebarProps) {
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  // Determine if we should poll based on execution status (treat pending as running)
  const shouldPoll =
    execution.status.toLowerCase() === 'pending' || execution.status.toLowerCase() === 'running';

  const { data: details, isPending: isLoadingDetails } = useExecutionDetails(
    execution.links.self,
    shouldPoll
  );

  // Also poll memory if execution details show pending/running status
  const shouldPollMemory =
    shouldPoll ||
    details?.status?.toLowerCase() === 'pending' ||
    details?.status?.toLowerCase() === 'running';

  const { data: memory, isPending: isLoadingMemory } = useMemoryDetails(
    details?.links?.memory,
    shouldPollMemory
  );

  const { data: memoryChunks, isPending: isLoadingChunks } = useMemoryChunks(
    memory?.links?.chunks,
    shouldPollMemory
  );

  const formatJsonForMarkdown = (obj: unknown): string => {
    return '```json\n' + JSON.stringify(obj, null, 2) + '\n```';
  };

  const canCancel = details?.status === 'pending' || details?.status === 'running' || isCancelling;

  const handleCancelClick = () => {
    setShowCancelConfirmation(true);
  };

  const handleConfirmCancel = () => {
    onCancel?.();
    setShowCancelConfirmation(false);
  };

  const handleCancelDialogClose = () => {
    setShowCancelConfirmation(false);
  };

  return (
    <ArtifactProvider>
      <ArtifactContainer>
        <Flex flexDirection="column" gap="16" p="16">
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
            <Flex flexDirection="row" alignItems="center" gap="12">
              <Text fontWeight="600" fontSize="lg">
                Execution Details
              </Text>
              {details && <ExecutionStatusBadge status={details.status} showAsRunning />}
            </Flex>
            <Flex flexDirection="row" gap="8" alignItems="center">
              {canCancel && (
                <Button
                  appearance="danger"
                  onClick={handleCancelClick}
                  disabled={isCancelling}
                  loading={isCancelling}
                  aria-label="Cancel workflow execution"
                >
                  Cancel Execution
                </Button>
              )}
              <Button
                icon={<IconXmark size="16" />}
                appearance="default"
                onClick={onClose}
                aria-label="Close sidebar"
              />
            </Flex>
          </Flex>
          {isLoadingDetails ? (
            <Flex justifyContent="center" py="24">
              <Spinner>Loading details...</Spinner>
            </Flex>
          ) : details ? (
            <>
              {/* Basic Info */}
              <Field label="Execution Details">
                <Box border="1" borderColor="border.default" rounded="md">
                  <Box bg="bg.default" p="12">
                    <MarkdownText
                      text={(() => {
                        const endedRow = details.end_time
                          ? `| **Ended** | ${new Date(details.end_time).toLocaleString()} |`
                          : '';
                        const durationRow = details.result?.execution_time
                          ? `| **Duration** | ${details.result.execution_time.toFixed(2)}s |`
                          : '';

                        return `| Field | Value |
|-------|--------|
| **Execution ID** | \`${details.id}\` |
| **Agent** | ${details.workflow_agent?.name || 'Unknown'} |
| **Started** | ${new Date(details.start_time).toLocaleString()} |
${endedRow}
${durationRow}`
                          .replace(/^\n+|\n+$/g, '')
                          .replace(/\n\n+/g, '\n');
                      })()}
                    />
                  </Box>
                </Box>
              </Field>

              {/* Payload */}
              <Disclosure defaultOpen>
                <DisclosureTrigger>
                  <Field label="Trigger Payload" info="Trigger data that initiated the execution" />
                </DisclosureTrigger>
                <DisclosureContent>
                  <Flex flexDirection="column" gap="16" px="16">
                    {details.trigger_info?.payload ? (
                      <Field label="Payload Data">
                        <Box border="1" borderColor="border.default" rounded="md">
                          <Box bg="bg.default" p="12">
                            <MarkdownText
                              text={formatJsonForMarkdown(details.trigger_info.payload)}
                            />
                          </Box>
                        </Box>
                      </Field>
                    ) : details.payload ? (
                      <Field label="Payload Data">
                        <Box border="1" borderColor="border.default" rounded="md">
                          <Box bg="bg.default" p="12">
                            <MarkdownText text={formatJsonForMarkdown(details.payload)} />
                          </Box>
                        </Box>
                      </Field>
                    ) : (
                      <EmptyStateMessage message="No trigger payload data available" />
                    )}
                  </Flex>
                </DisclosureContent>
              </Disclosure>
              {/* Artifacts - Flattened from all step executions */}
              {details.step_executions &&
                details.step_executions.length > 0 &&
                (() => {
                  const allArtifacts = flattenArtifacts(details.step_executions);

                  return allArtifacts.length > 0 ? (
                    <Disclosure defaultOpen>
                      <DisclosureTrigger>
                        <Field
                          label="Artifacts"
                          info={`${allArtifacts.length} artifact(s) generated during workflow execution`}
                        />
                      </DisclosureTrigger>
                      <DisclosureContent>
                        <Flex flexDirection="column" gap="16" px="16">
                          <Artifacts artifacts={allArtifacts} />
                        </Flex>
                      </DisclosureContent>
                    </Disclosure>
                  ) : null;
                })()}
              {/* Result */}
              <Disclosure defaultOpen>
                <DisclosureTrigger>
                  <Field label="Output" info="Execution results and response" />
                </DisclosureTrigger>
                <DisclosureContent>
                  <Flex flexDirection="column" gap="16" px="16">
                    <Field label="Execution Response">
                      <ExecutionOutputDisplay
                        result={details.result ? { response: details.result.response } : null}
                        showStatus={false}
                        emptyMessage="No output available."
                        emptySubMessage="Execution did not produce any results."
                      />
                    </Field>
                  </Flex>
                </DisclosureContent>
              </Disclosure>

              {/* Error */}
              {details.error_message && (
                <Disclosure defaultOpen>
                  <DisclosureTrigger>
                    <Field label="Error" info="Execution error details" />
                  </DisclosureTrigger>
                  <DisclosureContent>
                    <Flex flexDirection="column" gap="16" px="16">
                      <Field label="Error Details">
                        <Box border="1" borderColor="border.error" rounded="md">
                          <Flex flexDirection="column">
                            <Box bg="bg.error.subtle" p="12">
                              <Text fontSize="sm" fontWeight="600" color="fg.error">
                                Execution Failed
                              </Text>
                            </Box>
                            <Box bg="bg.default" p="12">
                              <Text fontSize="md" color="fg.error">
                                {details.error_message}
                              </Text>
                            </Box>
                          </Flex>
                        </Box>
                      </Field>
                    </Flex>
                  </DisclosureContent>
                </Disclosure>
              )}

              {/* Memory */}
              {details.links?.memory && (
                <Disclosure>
                  <DisclosureTrigger>
                    <Field label="Memory" info="Agent memory chunks from execution" />
                  </DisclosureTrigger>
                  <DisclosureContent>
                    <MemoryChunksDisplay
                      memoryChunks={memoryChunks?.slice(3) || []}
                      isLoading={isLoadingMemory || isLoadingChunks}
                      emptyMessage="No memory chunks available"
                      emptySubMessage="Memory data will appear here when available."
                      chunkCountLabel="Memory chunks"
                    />
                  </DisclosureContent>
                </Disclosure>
              )}

              {/* Token Usage */}
              <TokenUsageSection
                tokenUsage={
                  details.step_executions && details.step_executions.length > 0
                    ? getTotalTokenUsageForWorkflow(details)
                    : details.result?.token_usage
                }
              />
            </>
          ) : (
            <Text color="fg.secondary">Failed to load execution details</Text>
          )}

          <ConfirmationModal
            isShown={showCancelConfirmation}
            title="Cancel Workflow Execution"
            action="Cancel Execution"
            appearance="danger"
            onCancel={handleCancelDialogClose}
            onConfirm={handleConfirmCancel}
          >
            <Text>
              Are you sure you want to cancel this workflow execution? This action cannot be undone.
            </Text>
          </ConfirmationModal>
        </Flex>
      </ArtifactContainer>
    </ArtifactProvider>
  );
}
